import { apiClient } from "../../lib/api-client";
import axios from "axios";
import { useAuthStore } from "../../state/auth-store";
import type { UserIdentificationStatus } from "../../types";
import { normalizeIdentityDoc } from "./dni";

type PromptRequest = {
  currentDni?: string | null;
  errorMessageKey?: string;
  errorMessage?: string;
  errorStatusCode?: number;
};

type PromptHandler = (request: PromptRequest) => Promise<string>;

export class IdentityService {
  private promptHandler?: PromptHandler;

  registerPromptHandler(handler: PromptHandler) {
    this.promptHandler = handler;

    return () => {
      if (this.promptHandler === handler) {
        this.promptHandler = undefined;
      }
    };
  }

  async getIdentificationStatus(): Promise<UserIdentificationStatus> {
    const { data } = await apiClient.get<UserIdentificationStatus>(
      "/users/me/identification",
    );
    return data;
  }

  async updateDni(dni: string): Promise<UserIdentificationStatus> {
    const normalizedDni = normalizeIdentityDoc(dni);
    const { data } = await apiClient.patch<UserIdentificationStatus>(
      "/users/me/identification",
      { dni: normalizedDni },
    );

    if (data.dni) {
      useAuthStore.getState().setUserDni(data.dni);
    }

    return data;
  }

  async ensureUserIdentification(): Promise<UserIdentificationStatus> {
    let status = await this.getIdentificationStatus();
    let promptRequest: PromptRequest = {
      currentDni: status.dni,
    };

    if (status.isIdentificationComplete) {
      if (status.dni) {
        useAuthStore.getState().setUserDni(status.dni);
      }
      return status;
    }

    if (!this.promptHandler) {
      throw new Error("Identity prompt handler is not registered.");
    }

    while (!status.isIdentificationComplete) {
      const dni = await this.promptHandler(promptRequest);

      try {
        status = await this.updateDni(dni);
        promptRequest = {
          currentDni: status.dni,
        };
      } catch (error) {
        const errorStatusCode = axios.isAxiosError(error)
          ? error.response?.status
          : undefined;
        const errorMessage =
          axios.isAxiosError(error) &&
          typeof error.response?.data === "object" &&
          error.response?.data &&
          "message" in error.response.data
            ? error.response.data.message
            : undefined;

        promptRequest = {
          currentDni: normalizeIdentityDoc(dni),
          errorStatusCode,
          errorMessage: Array.isArray(errorMessage)
            ? errorMessage[0]
            : errorMessage,
          errorMessageKey: errorMessage
            ? undefined
            : "prompt.errors.saveFailed",
        };
      }
    }

    return status;
  }
}

export const identityService = new IdentityService();
